import { ReloadOutlined } from '@ant-design/icons';
import {
  EXCEPTION_KIND_LABEL,
  type ExceptionKind,
  type ExceptionRow,
  type ExceptionStatus,
} from '@serievo/contracts';
import { Alert, App, Button, Card, Flex, Form, Input, Modal, Segmented, Table, Tag, Tooltip, Typography } from 'antd';
import { useState } from 'react';
import { formatInt } from '../api/client';
import { useCloseException, useExceptions, useRunR1 } from '../api/reports';
import { Money, periodLabel, viDate } from '../components/ui';
import { useWork } from '../features/context/WorkContext';

const STATUS: Record<ExceptionStatus, { label: string; color: string }> = {
  OPEN: { label: 'Đang mở', color: 'red' },
  CLOSED: { label: 'Đã đóng', color: 'default' },
  RESOLVED: { label: 'Đã khớp', color: 'green' },
};

/** Tóm tắt một ngoại lệ thành câu dễ đọc từ detail của R1. */
function describe(e: ExceptionRow): string {
  const d = e.detail as {
    order?: { orderSn: string; deliveredDate: string };
    invoice?: { serial: string; number: number; issuedDate: string };
    invoices?: { serial: string; number: number }[];
    daysLate?: number;
  };
  const inv = d.invoice ? `${d.invoice.serial} số ${d.invoice.number}` : '';
  switch (e.kind) {
    case 'MISSING_INVOICE':
      return `Đơn ${d.order?.orderSn} giao ngày ${viDate(d.order?.deliveredDate)} chưa có hóa đơn`;
    case 'DUPLICATE_INVOICE':
      return `Đơn ${d.order?.orderSn} có ${d.invoices?.length} hóa đơn: ${d.invoices?.map((i) => i.number).join(', ')}`;
    case 'AMOUNT_MISMATCH':
      return `Hóa đơn ${inv} lệch tiền so với đơn ${d.order?.orderSn}`;
    case 'LATE_INVOICE':
      return `Hóa đơn ${inv} lập ngày ${viDate(d.invoice?.issuedDate)}, trễ ${d.daysLate} ngày so với ngày giao`;
    case 'RETURN_NOT_ADJUSTED':
      return `Đơn ${d.order?.orderSn} đã trả hàng, hóa đơn ${inv} chưa điều chỉnh giảm`;
    case 'INVOICE_WITHOUT_ORDER':
      return `Hóa đơn ${inv} không tìm thấy đơn sàn tương ứng`;
  }
}

/** Đối soát R1 đơn ↔ hóa đơn: danh sách ngoại lệ, đóng kèm kết luận, chạy lại. */
export function ReconPage() {
  const w = useWork();
  const { message } = App.useApp();
  const [status, setStatus] = useState<ExceptionStatus | ''>('OPEN');
  const [kind, setKind] = useState<ExceptionKind | ''>('');
  const [closing, setClosing] = useState<ExceptionRow | null>(null);
  const list = useExceptions(w, { status: status || undefined, kind: kind || undefined });
  const all = useExceptions(w, {});
  const run = useRunR1(w);
  const canResolve = w.perms.has('acct.exception.resolve');
  const canRun = w.perms.has('acct.recon.run');

  if (!/^\d{4}-\d{2}$/.test(w.period)) {
    return <Alert style={{ margin: 16 }} type="info" showIcon message="Đối soát R1 xem theo kỳ tháng. Chọn Tháng trên thanh ngữ cảnh." />;
  }

  const summary = all.data?.summary;
  return (
    <div className="page">
      <Flex justify="space-between" align="center" wrap gap={8}>
        <Typography.Title level={4} className="page-title">
          Đối soát R1 · đơn hàng ↔ hóa đơn · {periodLabel(w.period)}
        </Typography.Title>
        <Tooltip title={canRun ? undefined : 'Tài khoản chỉ có quyền xem'}>
          <Button
            icon={<ReloadOutlined />}
            disabled={!canRun}
            loading={run.isPending}
            onClick={() =>
              run.mutate(undefined, {
                onSuccess: (r) =>
                  void message.success(
                    `Đã chạy lại: ${formatInt(r.data.exceptions)} ngoại lệ, mở mới ${r.data.opened}, tự khớp ${r.data.resolved}`,
                  ),
                onError: (e) => void message.error(e.message),
              })
            }
          >
            Chạy lại R1
          </Button>
        </Tooltip>
      </Flex>

      <Card size="small">
        <Flex wrap gap={8} style={{ marginBottom: 12 }}>
          <Segmented
            value={status}
            onChange={(v) => setStatus(v as ExceptionStatus | '')}
            options={[
              { value: 'OPEN', label: `Đang mở (${summary?.byStatus.OPEN ?? 0})` },
              { value: 'CLOSED', label: `Đã đóng (${summary?.byStatus.CLOSED ?? 0})` },
              { value: 'RESOLVED', label: `Đã khớp (${summary?.byStatus.RESOLVED ?? 0})` },
              { value: '', label: 'Tất cả' },
            ]}
          />
        </Flex>
        <Flex wrap gap={6} style={{ marginBottom: 12 }}>
          <Tag.CheckableTag checked={kind === ''} onChange={() => setKind('')}>
            Mọi loại lỗi
          </Tag.CheckableTag>
          {(Object.keys(EXCEPTION_KIND_LABEL) as ExceptionKind[]).map((k) => (
            <Tag.CheckableTag key={k} checked={kind === k} onChange={() => setKind(kind === k ? '' : k)}>
              {EXCEPTION_KIND_LABEL[k]} {summary?.byKind[k] ? `(${summary.byKind[k]})` : ''}
            </Tag.CheckableTag>
          ))}
        </Flex>
        {list.error && <Alert type="error" showIcon message={list.error.message} />}
        <Table<ExceptionRow>
          size="small"
          rowKey="id"
          loading={list.isPending}
          dataSource={list.data?.items ?? []}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 900 }}
          columns={[
            {
              title: 'Loại lỗi',
              dataIndex: 'kind',
              width: 190,
              render: (k: ExceptionKind, r) => (
                <>
                  <Tag color={r.severity === 'BLOCKING' ? 'red' : 'gold'}>{r.severity === 'BLOCKING' ? 'Chặn' : 'Cảnh báo'}</Tag>
                  {EXCEPTION_KIND_LABEL[k]}
                </>
              ),
            },
            { title: 'Mô tả', key: 'desc', render: (_, r) => describe(r) },
            { title: 'Chênh lệch', dataIndex: 'diffAmount', align: 'right', width: 130, render: (v: string) => <Money v={v} /> },
            {
              title: 'Trạng thái',
              dataIndex: 'status',
              width: 110,
              render: (s: ExceptionStatus) => <Tag color={STATUS[s].color}>{STATUS[s].label}</Tag>,
            },
            {
              title: 'Kết luận',
              dataIndex: 'resolution',
              ellipsis: true,
              render: (v: string | null, r) =>
                r.status === 'OPEN' ? (
                  <Tooltip title={canResolve ? undefined : 'Tài khoản chỉ có quyền xem'}>
                    <Button size="small" disabled={!canResolve} onClick={() => setClosing(r)}>
                      Đóng kèm kết luận
                    </Button>
                  </Tooltip>
                ) : (
                  v ?? (r.status === 'RESOLVED' ? 'Dữ liệu đã khớp ở lần chạy sau' : '')
                ),
            },
          ]}
        />
      </Card>
      <CloseModal row={closing} onClose={() => setClosing(null)} />
    </div>
  );
}

function CloseModal({ row, onClose }: { row: ExceptionRow | null; onClose: () => void }) {
  const w = useWork();
  const close = useCloseException(w);
  const { message } = App.useApp();
  const [form] = Form.useForm<{ conclusion: string }>();
  return (
    <Modal
      open={!!row}
      title="Đóng ngoại lệ"
      okText="Đóng ngoại lệ"
      cancelText="Hủy"
      confirmLoading={close.isPending}
      destroyOnHidden
      onCancel={onClose}
      onOk={() =>
        void form.validateFields().then((v) =>
          close.mutate(
            { id: row!.id, conclusion: v.conclusion },
            {
              onSuccess: () => {
                void message.success('Đã đóng ngoại lệ');
                form.resetFields();
                onClose();
              },
              onError: (e) => void message.error(e.message),
            },
          ),
        )
      }
    >
      {row && (
        <Typography.Paragraph type="secondary">
          {EXCEPTION_KIND_LABEL[row.kind]}: {describe(row)}
        </Typography.Paragraph>
      )}
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="conclusion"
          label="Kết luận của kế toán"
          rules={[
            { required: true, message: 'Nhập kết luận' },
            { min: 10, message: 'Kết luận cần ít nhất 10 ký tự' },
          ]}
        >
          <Input.TextArea rows={4} maxLength={2000} showCount placeholder="Ví dụ: Đã lập hóa đơn bổ sung số …, hoặc lý do chấp nhận chênh lệch" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
